import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { toast } from "sonner";
import { Sparkles, Moon, Headphones, BookOpen, HeartHandshake, ArrowRight, ShoppingBag } from "lucide-react";

const INCLUDED = [
  { icon: Moon, title: "Year-ahead tarot map", desc: "A personal reading mapping the chapters of your next 12 months — recorded for you." },
  { icon: HeartHandshake, title: "1:1 intake call", desc: "A 60-minute intimate session to listen to what you're holding, before anything is built." },
  { icon: Headphones, title: "3 personalised guided meditations", desc: "Written and recorded by Mehjabin, using your name and intention. Yours forever." },
  { icon: BookOpen, title: "21-day daily ritual journal", desc: "A printable, beautifully-set sequence designed around your specific season." },
  { icon: Sparkles, title: "1:1 follow-up sessions", desc: "Continue the work beyond your 21 days — personal sessions tuned to your inner engineering, ₹2,000 per session." },
];

const FAQ = [
  {
    q: "Who is the Blueprint for?",
    a: "Someone in a tender chapter — a transition, a longing, a quiet readiness for change — who wants a slow, specific, hand-poured plan rather than a generic course.",
  },
  {
    q: "How is this different from a regular session?",
    a: "A session is a single moment. The Blueprint is twenty-one days of designed practice — built around the exact thing your intake call reveals.",
  },
  {
    q: "How soon can we begin?",
    a: "Within 5–7 days of booking, your intake call is scheduled. The full Blueprint is delivered 7–10 days after that. Spots are intentionally limited.",
  },
  {
    q: "Refund policy?",
    a: "Full refund within 48 hours of booking, before the intake call. After the intake call, refunds are case-by-case — but I always make it right.",
  },
];

export default function Blueprint() {
  usePageMeta(
    "The Just Be You Blueprint — Your Personal 21-Day Ritual | Just be you",
    "A bespoke 21-day manifestation blueprint hand-crafted by Mehjabin around your story — with 1:1 follow-up sessions to continue the work."
  );
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const { addItem } = useCart();
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/products/the-just-be-you-blueprint").then((r) => setProduct(r.data)).catch(() => {});
  }, []);

  const buyNow = async () => {
    if (!product) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", { product_id: product.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not open checkout");
      setLoading(false);
    }
  };

  if (!product) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-ink/50 font-serif italic">arriving…</div>;
  }

  const spotsAvail = typeof product.spots_remaining === "number" && product.spots_total;
  const spotsLine = spotsAvail
    ? `${product.spots_remaining} of ${product.spots_total} spots open this month`
    : null;

  return (
    <div data-testid="blueprint-page">
      {/* HERO */}
      <section className="relative pt-12 md:pt-20 pb-24 overflow-hidden">
        <div className="absolute -top-20 left-10 w-[520px] h-[520px] bg-peach/40 organic-blob blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 right-10 w-[420px] h-[420px] bg-lavender/40 organic-blob blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-lapis text-canvas text-[10px] uppercase tracking-[0.22em] px-4 py-1.5 rounded-full font-semibold mb-6" data-testid="blueprint-exclusive-pill">
              <Sparkles size={11} /> Only here · Signature offering
            </div>
            <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-ink">
              The Just Be You<br />
              <em className="text-peach-deep">Blueprint</em>.
            </h1>
            <p className="mt-8 text-lg text-ink/65 max-w-xl leading-relaxed">
              A bespoke 21-day manifestation ritual, hand-poured for one soul at a time.
              Not a course. Not a kit. A plan, made for the chapter you're actually in.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <button onClick={buyNow} disabled={loading} className="btn-primary" data-testid="blueprint-buy-btn">
                <ShoppingBag size={16} /> {loading ? "Opening…" : `Reserve a spot · ${format(product.price, product.price_inr)}`}
              </button>
              <Link to="/bookings" className="btn-soft" data-testid="blueprint-call-btn">
                Or speak with me first <ArrowRight size={14} />
              </Link>
            </div>

            {spotsLine && (
              <div className="mt-6 inline-flex items-center gap-2 text-sm bg-cream/70 px-4 py-2 rounded-full text-ink/75" data-testid="blueprint-spots-counter">
                <span className="w-2 h-2 rounded-full bg-sage-deep animate-pulse" />
                {spotsLine}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.15 }} className="lg:col-span-5 relative">
            <div className="absolute -inset-6 bg-sage/30 organic-blob blur-2xl" />
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-2xl shadow-lapis/15">
              <img src={product.image} alt={product.title} className="w-full h-full object-cover" data-testid="blueprint-image" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-cream/40 via-peach/15 to-lavender/30" data-testid="blueprint-included-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="overline mb-5">What you receive</div>
          <h2 className="font-serif text-4xl md:text-5xl text-lapis font-light leading-tight max-w-2xl">
            Five pieces, <em className="text-peach-deep">woven together</em>.
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {INCLUDED.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="glass p-8 rounded-[2rem] flex gap-5"
                data-testid={`blueprint-included-${i}`}
              >
                <Icon size={32} className="text-lapis flex-shrink-0" strokeWidth={1.3} />
                <div>
                  <h3 className="font-serif text-2xl text-ink mb-2">{title}</h3>
                  <p className="text-sm text-ink/65 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* THE PROMISE */}
      <section className="py-20 md:py-28 max-w-4xl mx-auto px-6 md:px-12 text-center relative" data-testid="blueprint-promise-section">
        <div className="overline mb-6">My promise</div>
        <h2 className="font-serif text-4xl md:text-5xl text-ink italic font-light leading-tight">
          This is built for one person.<br /><span className="text-lapis not-italic">You.</span>
        </h2>
        <p className="mt-8 text-lg text-ink/70 leading-relaxed max-w-2xl mx-auto">
          {product.long_description}
        </p>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-cream/30" data-testid="blueprint-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="overline mb-5">Soft questions</div>
          <h2 className="font-serif text-4xl text-lapis font-light leading-tight mb-12">
            Before you choose.
          </h2>
          <div className="space-y-6">
            {FAQ.map((f, i) => (
              <details key={i} className="glass p-6 rounded-[1.5rem] group" data-testid={`blueprint-faq-${i}`}>
                <summary className="font-serif text-xl text-ink cursor-pointer flex justify-between items-center list-none">
                  {f.q}
                  <span className="text-lapis text-2xl group-open:rotate-45 transition-transform duration-300">+</span>
                </summary>
                <p className="mt-4 text-ink/65 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-peach/30 organic-blob blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <div className="overline mb-6">When you're ready</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink italic font-light leading-tight">
            Twenty-one days,<br /><span className="text-peach-deep not-italic">made for you.</span>
          </h2>
          <p className="mt-8 text-ink/65 max-w-xl mx-auto">
            {spotsLine || "A handful of spots open each month — held intentionally."}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={buyNow} disabled={loading} className="btn-primary" data-testid="blueprint-bottom-buy-btn">
              <Sparkles size={14} /> {loading ? "Opening…" : `Reserve your spot · ${format(product.price, product.price_inr)}`}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
